import { HelpCircle, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Info, Download, FileSpreadsheet } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

interface ContextualHelpPanelProps {
  isExpanded: boolean;
  onToggle: (expanded: boolean) => void;
  onDownloadTemplate?: () => void;
}

/**
 * ContextualHelpPanel - Enhanced collapsible help panel
 * Default collapsed for compact UI, prominent toggle button
 */
export default function ContextualHelpPanel({
  isExpanded,
  onToggle,
  onDownloadTemplate,
}: ContextualHelpPanelProps) {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full px-3 py-1.5 flex items-center justify-between hover:bg-muted/50 transition-colors border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-blue-100 flex items-center justify-center">
            <HelpCircle className="h-3 w-3 text-blue-600" />
          </div>
          <span className="text-xs font-medium">Instructions & Help</span>
          {!isExpanded && (
            <span className="text-[10px] text-muted-foreground">(click to expand)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onDownloadTemplate && !isExpanded && (
            <div
              className="h-6 px-2 text-xs gap-1 flex items-center rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadTemplate();
              }}
            >
              <Download className="h-3 w-3" />
              Template
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-3 py-2 grid grid-cols-4 gap-3 text-xs border-b border-border bg-muted/10">
          {/* Required Fields */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              <span className="font-medium text-[11px]">Required Fields</span>
            </div>
            <ul className="space-y-0.5 text-[10px] text-muted-foreground pl-4">
              <li>• Account # (unique)</li>
              <li>• Commodity Type</li>
              <li>• Vendor Name</li>
              <li>• Property Name</li>
            </ul>
          </div>
          
          {/* Optional Fields */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Info className="h-3 w-3 text-blue-500" />
              <span className="font-medium text-[11px]">Optional Fields</span>
            </div>
            <ul className="space-y-0.5 text-[10px] text-muted-foreground pl-4">
              <li>• Property Code</li>
              <li>• Meter #, GL Code</li>
              <li>• Service Address</li>
              <li>• Notes</li>
            </ul>
          </div>
          
          {/* Best Practices */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              <span className="font-medium text-[11px]">Best Practices</span>
            </div>
            <ul className="space-y-0.5 text-[10px] text-muted-foreground pl-4">
              <li>• Download template first</li>
              <li>• Max 10,000 rows</li>
              <li>• Double-click to edit</li>
              <li>• Tab to navigate</li>
            </ul>
          </div>
          
          {/* Quick Actions */}
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <FileSpreadsheet className="h-3 w-3 text-primary" />
              <span className="font-medium text-[11px]">Quick Actions</span>
            </div>
            <div className="flex flex-col gap-1">
              {onDownloadTemplate && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] gap-1 justify-start"
                  onClick={onDownloadTemplate}
                >
                  <Download className="h-3 w-3" />
                  Download Template
                </Button>
              )}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}